import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "./GoogleCSE.css";

// ── Style injection ───────────────────────────────────────────────────────────
const injectResultsStyle = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("cse-override-style")) return;

  const style = document.createElement("style");
  style.id = "cse-override-style";
  style.textContent = `
    .gsc-results-wrapper-overlay {
      position: static !important;
      background: transparent !important;
      background-color: transparent !important;
      border: none !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow: visible !important;
      top: auto !important; left: auto !important;
      right: auto !important; z-index: auto !important;
    }
    .gsc-modal-background-image,
    .gsc-modal-background-image-visible {
      display: none !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    .gsc-results-close-btn,
    .gsc-results-close-btn-visible { display: none !important; }

    .gsc-results-wrapper-nooverlay, .gsc-results,
    .gsc-webResult, .gsc-result, .gs-result,
    .gsc-above-wrapper-area, .gsc-below-wrapper-area, .gsc-wrapper {
      background: transparent !important;
      background-color: transparent !important;
      pointer-events: auto !important;
    }
    .gsc-webResult.gsc-result {
      pointer-events: auto !important;
      cursor: pointer !important;
      position: relative !important;
      z-index: 2 !important;
    }
    .gsc-webResult.gsc-result * { pointer-events: auto !important; }
    .gsc-webResult.gsc-result .gs-title a::before {
      content: ''; position: absolute !important;
      inset: 0 !important; z-index: 0 !important; display: block !important;
    }
    .gsc-webResult.gsc-result .gs-title,
    .gsc-webResult.gsc-result .gs-title a,
    .gsc-webResult.gsc-result .gs-title b,
    .gsc-webResult.gsc-result .gs-snippet,
    .gsc-webResult.gsc-result .gsc-url-top {
      pointer-events: auto !important;
      cursor: pointer !important;
      position: relative !important;
      z-index: 1 !important;
    }
  `;
  document.head.appendChild(style);
};

// ── SearchOverlay ─────────────────────────────────────────────────────────────
const SearchOverlay = ({ isOpen, onClose }) => {
  const panelRef = useRef(null);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getWrapper = () =>
    document.querySelector(
      ".gsc-results-wrapper-overlay, .gsc-results-wrapper-nooverlay",
    );

  // Reveal the results wrapper — call after search fires
  const showResults = useCallback(() => {
    const wrapper = getWrapper();
    if (wrapper) wrapper.style.display = "";
    // Also restore autocomplete if Google showed it
    document.querySelectorAll(".gsc-completion-container").forEach((el) => {
      el.style.display = "";
    });
  }, []);

  // ── Clear on close ─────────────────────────────────────────────────────────
  // NEVER destroy innerHTML. Just hide the results and clear the input.
  // Google CSE's DOM stays 100% intact so new searches always work.
  const clearSearch = useCallback(() => {
    // 1. Clear input text
    document.querySelectorAll("input.gsc-input").forEach((inp) => {
      inp.value = "";
      inp.dispatchEvent(new Event("input", { bubbles: true }));
      inp.dispatchEvent(new Event("change", { bubbles: true }));
    });
    // 2. Hide results wrapper in place (don't move or delete it)
    const wrapper = getWrapper();
    if (wrapper) wrapper.style.display = "none";
    // 3. Hide autocomplete / suggestions dropdown
    document.querySelectorAll(".gsc-completion-container").forEach((el) => {
      el.style.display = "none";
    });
  }, []);

  // Forcefully remove any body scroll-lock styles that Google CSE may have set
  const unlockBodyScroll = useCallback(() => {
    const scrollY = document.body.style.top;
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    // Also remove any class Google CSE adds to lock scrolling
    document.body.classList.remove(
      "gsc-overflow-hidden",
      "gsc-modal-background-image-visible",
    );
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
  }, []);

  const handleClose = useCallback(() => {
    clearSearch();
    // Flush body lock immediately — don't wait for React state + effect cycle
    unlockBodyScroll();
    onClose();
  }, [clearSearch, unlockBodyScroll, onClose]);

  // ── Auto-focus input when overlay opens ────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      const input = document.querySelector(".search-overlay input.gsc-input");
      if (input) input.focus();
    }, 150);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // ── Show results when user submits search (Enter or button click) ──────────
  // Google CSE fires the search internally; we just need to un-hide the wrapper
  // after a short delay once Google has populated it.
  useEffect(() => {
    if (!isOpen) return;

    const reveal = () => setTimeout(showResults, 120);

    // Enter key in the search input
    const onKey = (e) => {
      if (e.key === "Enter" && e.target.closest("input.gsc-input")) reveal();
    };
    // Click on Google's search button
    const onClick = (e) => {
      if (
        e.target.closest(".gsc-search-button-v2") ||
        e.target.closest(".gsc-search-button")
      )
        reveal();
    };

    document.addEventListener("keydown", onKey, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [isOpen, showResults]);

  // ── Escape key ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose]);

  // ── Click delegation: whole result card navigates ─────────────────────────
  const handlePanelClick = (e) => {
    const card = e.target.closest(".gsc-webResult.gsc-result");
    if (!card) return;
    const titleLink = card.querySelector(".gs-title a");
    if (!titleLink) return;
    const href = titleLink.getAttribute("href") || titleLink.href;
    if (!href) return;
    if (e.target.closest(".gs-title a")) {
      handleClose();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    handleClose();
    window.location.href = href;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className={`search-overlay${isOpen ? " is-open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Site search"
      aria-hidden={!isOpen}
    >
      {/* Dark backdrop */}
      <div className="search-overlay-bg" />

      {/* Close button */}
      <button
        className="search-overlay-close"
        onClick={handleClose}
        aria-label="Close search"
        tabIndex={isOpen ? 0 : -1}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      {/* Panel — Google CSE renders everything inside gcse-search naturally */}
      <div
        className="search-overlay-panel"
        ref={panelRef}
        onClick={handlePanelClick}
      >
        <p className="search-overlay-label">What are you looking for?</p>
        {/*
          gcse-search must always be in the DOM so Google CSE initialises on
          page load. We NEVER move or destroy any of Google's DOM nodes —
          we only hide/show the results wrapper via style.display.
        */}
        <div className="gcse-search" />
      </div>
    </div>,
    document.body,
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
const CSE = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!document.querySelector('script[src*="cse.google.com"]')) {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://cse.google.com/cse.js?cx=230581f36406547f0";
      document.body.appendChild(script);
    }
    injectResultsStyle();
  }, []);

  // Lock body scroll — save/restore position to prevent page jump
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      return () => {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
      };
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      if (scrollY) window.scrollTo(0, parseInt(scrollY || "0") * -1);

      // Safety net: Google CSE may asynchronously re-apply body locks after
      // its own overlay closes. Watch for that and immediately revert it.
      const observer = new MutationObserver(() => {
        const pos = document.body.style.position;
        const ov = document.body.style.overflow;
        if (pos === "fixed" || ov === "hidden") {
          document.body.style.overflow = "";
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
          document.body.classList.remove(
            "gsc-overflow-hidden",
            "gsc-modal-background-image-visible",
          );
        }
      });
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });
      // Stop watching after 500 ms — long enough to catch delayed CSE changes
      const timer = setTimeout(() => observer.disconnect(), 500);
      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
  }, [isOpen]);

  return (
    <>
      <button
        className="cicSearch-button"
        onClick={() => setIsOpen(true)}
        aria-label="Open search"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
      </button>

      {mounted && (
        <SearchOverlay isOpen={isOpen} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
};

export default CSE;
